import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { comoSeGuardara, formatearMonto } from "@/lib/montos";
import { cn } from "@/lib/utils";

import { totalesDeLaInstalacion } from "./cuadroDeInstalaciones";
import type { FormularioDeInstalacion } from "./instalaciones";

type ClaveDelFormulario = keyof FormularioDeInstalacion;

interface CamposDeInstalacionProps {
  /**
   * Prefijo de los `id` de los controles.
   *
   * EL FORMULARIO EN LÍNEA Y EL MODAL DE EDICIÓN CONVIVEN EN EL DOM. Con el
   * modal abierto, el formulario de la lista puede seguir montado detrás, así
   * que sin prefijos distintos habría dos controles con el mismo `id` y
   * `getByLabelText` (o cualquier tecnología asistiva) encontraría dos.
   */
  idPrefijo: string;
  formulario: FormularioDeInstalacion;
  ocupada: boolean;
  escribir: (cambio: Partial<FormularioDeInstalacion>) => void;
  /** Los obligatorios: se resaltan en ámbar mientras estén vacíos. */
  requeridos?: ClaveDelFormulario[];
}

interface CampoProps {
  id: string;
  etiqueta: string;
  unidad?: string;
  placeholder: string;
  inputMode?: "numeric" | "decimal";
  valor: string;
  clase?: string;
  faltante: boolean;
  disabled: boolean;
  /** Una lectura bajo el campo: cómo se va a guardar lo escrito. */
  lectura?: string | null;
  onChange: (valor: string) => void;
}

function Campo({
  id,
  etiqueta,
  unidad,
  placeholder,
  inputMode,
  valor,
  clase,
  faltante,
  disabled,
  lectura,
  onChange,
}: CampoProps) {
  return (
    <div className={cn("flex min-w-0 flex-col gap-1.5", clase)}>
      <Label htmlFor={id} className="mb-0 items-center gap-1.5">
        {etiqueta}
        {unidad ? <span className="text-ink-hint font-semibold">{unidad}</span> : null}
      </Label>
      <Input
        id={id}
        inputMode={inputMode}
        value={valor}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className={cn(faltante && "border-warning-dot/60")}
      />
      {/* LA LECTURA ES LO QUE HACE VISIBLE LA AMBIGÜEDAD DEL PUNTO. "1.000"
          acá son mil, pero quien copia de una pantalla en inglés escribe
          "2.50" queriendo decir dos coma cinco. */}
      {lectura ? <p className="text-ink-hint text-caption">Se guardará {lectura} por unidad</p> : null}
    </div>
  );
}

function usarCampo({ formulario, ocupada, escribir, requeridos = [] }: CamposDeInstalacionProps) {
  return (clave: ClaveDelFormulario) => ({
    valor: formulario[clave],
    faltante: requeridos.includes(clave) && formulario[clave].trim() === "",
    disabled: ocupada,
    onChange: (valor: string) => escribir({ [clave]: valor }),
  });
}

/**
 * Identificación: tipo, año y cantidad.
 *
 * LA CANTIDAD ES DECIMAL y su unidad la decide la obra —metros cuadrados de
 * losa, metros lineales de cierro, unidades de estanque—, por eso el rótulo
 * nombra las tres en vez de imponer una.
 */
export function GrupoDeIdentificacionDeInstalacion(props: CamposDeInstalacionProps) {
  const campo = usarCampo(props);

  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      <Campo
        id={`${props.idPrefijo}-tipo`}
        etiqueta="Tipo de instalación u obra"
        placeholder="Ej.: Losas de circulación"
        clase="col-span-2"
        {...campo("tipo")}
      />
      <Campo
        id={`${props.idPrefijo}-anio`}
        etiqueta="Año"
        inputMode="numeric"
        placeholder="Ej.: 2000"
        {...campo("anio")}
      />
      <Campo
        id={`${props.idPrefijo}-cantidad`}
        etiqueta="Cantidad"
        unidad="m², ml o un."
        inputMode="decimal"
        placeholder="Ej.: 1.000,00"
        {...campo("cantidad")}
      />
    </div>
  );
}

/**
 * Valorización: los dos valores por unidad, en UF.
 *
 * SON INDEPENDIENTES y ninguno se deriva del otro: el de reposición es lo que
 * cuesta hacer la obra de nuevo y el comercial lo que aporta al predio. No hay
 * vida útil ni ajuste, que es lo que separa esta pestaña de sus hermanas.
 */
export function GrupoDeValorizacionDeInstalacion(props: CamposDeInstalacionProps) {
  const campo = usarCampo(props);

  return (
    <div className="grid grid-cols-2 gap-3">
      <Campo
        id={`${props.idPrefijo}-reposicion`}
        etiqueta="Valor reposición"
        unidad="UF/un."
        inputMode="decimal"
        placeholder="Ej.: 2,50"
        lectura={comoSeGuardara(props.formulario.valor_reposicion_uf_un, "UF")}
        {...campo("valor_reposicion_uf_un")}
      />
      <Campo
        id={`${props.idPrefijo}-comercial`}
        etiqueta="Valor comercial"
        unidad="UF/un."
        inputMode="decimal"
        placeholder="Ej.: 0,81"
        lectura={comoSeGuardara(props.formulario.valor_comercial_uf_un, "UF")}
        {...campo("valor_comercial_uf_un")}
      />
    </div>
  );
}

/**
 * "Totales estimados": lo que el servidor va a calcular con lo escrito.
 *
 * SON DOS CIFRAS, una por valor, y la que no tiene su unitario queda en "—":
 * mostrar el comercial deducido del de reposición sería inventar el dato que
 * esta pestaña existe para no inventar.
 */
export function TotalesEstimados({ formulario }: { formulario: FormularioDeInstalacion }) {
  const { reposicion, comercial } = totalesDeLaInstalacion(formulario);
  const ninguno = reposicion === null && comercial === null;
  const cifra = (rotulo: string, valor: number | null) =>
    `${rotulo} ${valor === null ? "—" : `${formatearMonto(valor, null)} UF`}`;

  return (
    <div
      className={cn(
        "bg-card flex flex-wrap items-center gap-3.5 rounded-xl border-[1.5px] px-3.5 py-2.5",
        ninguno ? "border-divisor" : "border-success-dot/40",
      )}
    >
      <div className="min-w-0 flex-1">
        <div className="text-ink-muted text-[0.65625rem] font-extrabold tracking-[0.06em] uppercase">
          Totales estimados
        </div>
        <p className="text-ink-muted mt-0.5 text-[0.6875rem] font-semibold text-pretty">
          {ninguno
            ? "Se calculan al completar la cantidad y un valor por unidad. Ninguno se deduce del otro."
            : "Cantidad × valor por unidad, para cada uno. Ninguno se deduce del otro."}
        </p>
      </div>
      <div className="flex flex-none flex-col items-end gap-0.5 font-mono font-bold">
        <span className={cn("text-base", reposicion === null ? "text-ink-ghost" : "text-ink")}>
          {cifra("Rep.", reposicion)}
        </span>
        <span className={cn("text-base", comercial === null ? "text-ink-ghost" : "text-ink")}>
          {cifra("Com.", comercial)}
        </span>
      </div>
    </div>
  );
}
